import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';
import { Card, Pill } from './ui';
import { useInstatDesign } from './theme/instatDesign';
import { getMediaUrl } from '../../utils/mediaUrl';
import TelestrationEditor from './Telestration/TelestrationEditor';
import ClipCard from './ClipCard';
import ClipShelf from './ClipShelf';

function formatBytes(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function formatDuration(sec) {
  if (sec == null) return '';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const DAY = 86_400_000;

// Returns one of: 'today' | 'week' | 'month' | 'older'
function bucketByDate(ts) {
  if (!ts) return 'older';
  const ageMs = Date.now() - new Date(ts).getTime();
  if (ageMs < DAY) return 'today';
  if (ageMs < 7 * DAY) return 'week';
  if (ageMs < 30 * DAY) return 'month';
  return 'older';
}

// Filtered by local `playerId` when embedded in PlayerProfile's Instat tab so
// the list contains only clips linked to that scouting player. Without the
// prop (standalone /instat-poc usage) we fall back to the full club-scoped
// list. Orphan clips (`player_id IS NULL`) are intentionally hidden in the
// embedded mode — they'd be confusing without a way to link them.
export default function ClipsTab({
  localPlayerId = null,
  instatPlayerId = null,
  onCountChange = null,
} = {}) {
  const d = useInstatDesign();
  const { t } = useTranslation();

  const [savedClips, setSavedClips] = useState(null);
  const [savedLoading, setSavedLoading] = useState(false);
  const [playingClip, setPlayingClip] = useState(null);

  const reloadSavedClips = useCallback(async () => {
    setSavedLoading(true);
    try {
      const params = {};
      if (localPlayerId) params.playerId = localPlayerId;
      else if (instatPlayerId) params.instatPlayerId = instatPlayerId;
      const { data } = await api.get('/api/instat/clips', { params });
      setSavedClips(data.clips || []);
    } catch (e) {
      console.warn('failed to load saved clips:', e.message);
      setSavedClips([]);
    } finally {
      setSavedLoading(false);
    }
  }, [localPlayerId, instatPlayerId]);

  useEffect(() => {
    reloadSavedClips();
  }, [reloadSavedClips]);

  // Report count up to the parent so the tab badge can show it.
  useEffect(() => {
    if (onCountChange) onCountChange(savedClips ? savedClips.length : null);
  }, [savedClips, onCountChange]);

  const deleteSavedClip = async (clip) => {
    const name = clip.title || clip.fname;
    if (!window.confirm(t('instat.clips.deleteConfirm', { name }))) return;
    try {
      await api.delete(`/api/instat/clips/${clip.id}`);
      await reloadSavedClips();
    } catch (e) {
      console.error('delete failed:', e);
    }
  };

  // Server already returns clips sorted DESC by created_at — so the first
  // entry is the hero, the next 3 fill "Up Next", and groups preserve order.
  const hero = savedClips && savedClips[0];
  const upNext = useMemo(
    () => (savedClips ? savedClips.slice(1, 4) : []),
    [savedClips],
  );

  const groups = useMemo(() => {
    const acc = { today: [], week: [], month: [], older: [] };
    (savedClips || []).forEach((c) => {
      acc[bucketByDate(c.created_at)].push(c);
    });
    return [
      { key: 'today', label: t('instat.clips.sectionToday'), items: acc.today },
      { key: 'week',  label: t('instat.clips.sectionWeek'),  items: acc.week },
      { key: 'month', label: t('instat.clips.sectionMonth'), items: acc.month },
      { key: 'older', label: t('instat.clips.sectionOlder'), items: acc.older },
    ].filter((g) => g.items.length > 0);
  }, [savedClips, t]);

  return (
    <Box>
      {savedLoading && !savedClips && (
        <Card sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={16} sx={{ color: d.colors.textSecondary }} />
            <Typography sx={{ ...d.typo.caption }}>{t('common.loading')}</Typography>
          </Stack>
        </Card>
      )}

      {savedClips && savedClips.length === 0 && (
        <Card sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ ...d.typo.title }}>{t('instat.clips.title')}</Typography>
            <Tooltip title={t('instat.clips.refresh')}>
              <span>
                <IconButton size="small" onClick={reloadSavedClips} disabled={savedLoading}>
                  {savedLoading
                    ? <CircularProgress size={16} sx={{ color: d.colors.textSecondary }} />
                    : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Typography sx={{ ...d.typo.caption }}>{t('instat.clips.empty')}</Typography>
        </Card>
      )}

      {hero && (
        <HeroSection
          hero={hero}
          upNext={upNext}
          totalCount={savedClips.length}
          onPlay={setPlayingClip}
          onRefresh={reloadSavedClips}
          refreshing={savedLoading}
          onDelete={deleteSavedClip}
          t={t}
          d={d}
        />
      )}

      {groups.map((g) => (
        <ClipShelf
          key={g.key}
          label={g.label}
          clips={g.items}
          onPlay={setPlayingClip}
          onDelete={deleteSavedClip}
          t={t}
        />
      ))}

      <TelestrationEditor
        clip={playingClip}
        open={!!playingClip}
        onClose={() => setPlayingClip(null)}
        playlist={savedClips || []}
        onNavigate={setPlayingClip}
        onTagsChange={(clipId, tags) => {
          setSavedClips((prev) =>
            prev ? prev.map((c) => (c.id === clipId ? { ...c, tags } : c)) : prev,
          );
          setPlayingClip((prev) => (prev && prev.id === clipId ? { ...prev, tags } : prev));
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
//  Hero — large featured clip (the most recently saved) plus a compact
//  "Up Next" stack on the right with up to 3 follow-up clips.
// ---------------------------------------------------------------------------
function HeroSection({ hero, upNext, totalCount, onPlay, onRefresh, refreshing, onDelete, t, d }) {
  return (
    <Card sx={{ mb: 2.5, overflow: 'hidden' }} padding={0}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' },
          gap: 0,
        }}
      >
        {/* Hero player */}
        <Box>
          <Box
            onClick={() => onPlay(hero)}
            sx={{
              position: 'relative',
              cursor: 'pointer',
              background: '#000',
              aspectRatio: '16 / 9',
              '&:hover .play-overlay': { opacity: 1, transform: 'scale(1)' },
              '&:hover .play-icon':    { transform: 'scale(1.1)' },
            }}
          >
            <video
              key={hero.id}
              src={getMediaUrl('instat_clips', hero.filename)}
              preload="metadata"
              muted
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
              }}
            />
            {/* Bottom gradient + meta overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.75) 100%)',
                pointerEvents: 'none',
              }}
            />
            {/* Featured pill (top-left) */}
            <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
              <Pill tone="accent" size="sm">
                {t('instat.clips.featured')}
              </Pill>
            </Box>
            {/* Duration pill (top-right) */}
            {hero.duration != null && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  px: 1,
                  py: 0.25,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  borderRadius: `${d.radius.sm}px`,
                  fontSize: 12,
                  fontWeight: 600,
                  ...d.typo.tabular,
                }}
              >
                {formatDuration(hero.duration)}
              </Box>
            )}
            {/* Centered play button */}
            <Box
              className="play-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.85,
                transform: 'scale(1)',
                transition: `opacity ${d.motion.base}, transform ${d.motion.base}`,
              }}
            >
              <Box
                className="play-icon"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.92)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: d.shadows.cardStrong,
                  transition: `transform ${d.motion.base}`,
                }}
              >
                <PlayArrowRoundedIcon sx={{ fontSize: 44, color: '#111', ml: 0.5 }} />
              </Box>
            </Box>
            {/* Bottom title overlay */}
            <Box
              sx={{
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: 14,
                color: '#fff',
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0.1,
                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                }}
                noWrap
              >
                {hero.match_title || hero.fname}
              </Typography>
              {hero.title && (
                <Typography
                  sx={{
                    fontSize: 13,
                    opacity: 0.85,
                    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                  }}
                  noWrap
                >
                  {hero.title}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Hero meta strip (below video, inside hero column) */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.25, gap: 1 }}
          >
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {hero.quality && <Pill tone="outline" size="sm">{hero.quality}p</Pill>}
              {hero.file_size != null && (
                <Pill tone="outline" size="sm">{formatBytes(hero.file_size)}</Pill>
              )}
              <Pill tone="outline" size="sm">{totalCount} clips</Pill>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t('instat.clips.refresh')}>
                <span>
                  <IconButton size="small" onClick={onRefresh} disabled={refreshing}>
                    {refreshing
                      ? <CircularProgress size={14} sx={{ color: d.colors.textSecondary }} />
                      : <RefreshRoundedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('instat.clips.delete')}>
                <IconButton size="small" onClick={() => onDelete(hero)}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Up Next sidebar */}
        {upNext.length > 0 && (
          <Box
            sx={{
              borderLeft: { md: `1px solid ${d.colors.divider}` },
              borderTop: { xs: `1px solid ${d.colors.divider}`, md: 'none' },
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: d.colors.surfaceAlt,
            }}
          >
            <Typography
              sx={{
                ...d.typo.micro,
                color: d.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                px: 0.5,
                pb: 0.25,
              }}
            >
              {t('instat.clips.upNext')}
            </Typography>
            {upNext.map((c) => (
              <UpNextItem key={c.id} clip={c} onPlay={onPlay} d={d} />
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

function UpNextItem({ clip, onPlay, d }) {
  return (
    <Box
      onClick={() => onPlay(clip)}
      sx={{
        display: 'flex',
        gap: 1,
        cursor: 'pointer',
        borderRadius: `${d.radius.md}px`,
        p: 0.5,
        transition: `background ${d.motion.fast}`,
        '&:hover': { background: d.colors.canvas },
        '&:hover .un-thumb': { transform: 'scale(1.02)' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 96,
          minWidth: 96,
          aspectRatio: '16 / 9',
          borderRadius: `${d.radius.sm}px`,
          overflow: 'hidden',
          background: '#000',
        }}
      >
        <Box
          className="un-thumb"
          sx={{
            position: 'absolute',
            inset: 0,
            transition: `transform ${d.motion.base}`,
          }}
        >
          <video
            src={getMediaUrl('instat_clips', clip.filename)}
            preload="metadata"
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.20)',
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 24, color: '#fff' }} />
        </Box>
        {clip.duration != null && (
          <Box
            sx={{
              position: 'absolute',
              right: 3, bottom: 3,
              px: 0.5, py: 0.1,
              bgcolor: 'rgba(0,0,0,0.65)',
              color: '#fff',
              borderRadius: 0.5,
              fontSize: 10,
              fontWeight: 600,
              ...d.typo.tabular,
            }}
          >
            {formatDuration(clip.duration)}
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 }}>
          {clip.match_title || clip.fname}
        </Typography>
        <Typography noWrap sx={{ ...d.typo.caption }}>
          {clip.title || `${clip.quality || ''}${clip.quality ? 'p' : ''}`}
        </Typography>
      </Box>
    </Box>
  );
}


